// Mirrors webapp/src/theme.ts. Upstream pushes the palette onto :root as
// `--center-channel-bg-rgb` etc.; we keep the same variable names our Tailwind
// config reads (see src/styles/index.css) plus upstream aliases.

import {Constants} from './constants'

export type Theme = {
    mainBg: string
    mainFg: string
    buttonBg: string
    buttonFg: string
    sidebarBg: string
    sidebarFg: string
    sidebarTextActiveBorder: string
    sidebarWhiteLogo: string
    link: string
    linkVisited: string
    propDefault: string
    propGray: string
    propBrown: string
    propOrange: string
    propYellow: string
    propGreen: string
    propBlue: string
    propPurple: string
    propPink: string
    propRed: string
}

export const systemThemeName = 'system-theme'
export const defaultThemeName = 'default-theme'
export const darkThemeName = 'dark-theme'
export const lightThemeName = 'light-theme'

export const defaultTheme: Theme = {
    mainBg: '255, 255, 255',
    mainFg: '63, 67, 80',
    buttonBg: '28, 88, 217',
    buttonFg: '255, 255, 255',
    sidebarBg: '30, 50, 92',
    sidebarFg: '255, 255, 255',
    sidebarTextActiveBorder: '93, 137, 243',
    sidebarWhiteLogo: 'true',
    link: '93, 137, 234',
    linkVisited: '#551a8b',
    propDefault: '#fff',
    propGray: '#EDEDED',
    propBrown: '#F7DDC3',
    propOrange: '#ffd3c1',
    propYellow: '#f7f0b6',
    propGreen: '#c7eac3',
    propBlue: '#B1D1F6',
    propPurple: '#e6d0ff',
    propPink: '#ffd6e9',
    propRed: '#ffa9a9',
}

export const darkTheme: Theme = {
    ...defaultTheme,
    mainBg: '55, 53, 47',
    mainFg: '220, 220, 220',
    buttonBg: '80, 170, 221',
    sidebarBg: '75, 73, 67',
    sidebarTextActiveBorder: '102, 185, 167',
    link: '#0090ff',
    linkVisited: 'hsla(270, 68%, 70%, 1.0)',
    propDefault: 'hsla(0, 100%, 100%, 0.08)',
    propGray: 'hsla(0, 0%, 70%, 0.4)',
    propBrown: 'hsla(25, 60%, 40%, 0.4)',
    propOrange: 'hsla(35, 100%, 50%, 0.4)',
    propYellow: 'hsla(48, 100%, 70%, 0.4)',
    propGreen: 'hsla(120, 100%, 70%, 0.4)',
    propBlue: 'hsla(240, 100%, 70%, 0.4)',
    propPurple: 'hsla(270, 100%, 64%, 0.4)',
    propPink: 'hsla(310, 100%, 80%, 0.4)',
    propRed: 'hsla(4, 100%, 70%, 0.4)',
}

export const lightTheme: Theme = {
    ...defaultTheme,
    mainBg: '255, 255, 255',
    mainFg: '55, 53, 47',
    buttonBg: '80, 170, 221',
    sidebarBg: '247, 246, 243',
    sidebarFg: '55, 53, 47',
    sidebarTextActiveBorder: '87, 158, 255',
    sidebarWhiteLogo: 'false',
}

let activeThemeName: string = defaultThemeName

const toRgbTriplet = (value: string): string => {
    const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(value.trim())
    if (!m) {
        return value
    }
    return `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}`
}

// Tailwind's `rgb(var(--x) / <alpha-value>)` only parses space-separated
// channels, so the variables it reads must not carry commas.
const toSpaceTriplet = (value: string): string => {
    const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(value.trim())
    if (!m) {
        return value.split(',').map((part) => part.trim()).join(' ')
    }
    return `${parseInt(m[1], 16)} ${parseInt(m[2], 16)} ${parseInt(m[3], 16)}`
}

export function getActiveThemeName(): string {
    return activeThemeName
}

export function applyTheme(theme: Theme): Theme {
    const consolidated: Theme = {...defaultTheme, ...theme}
    const root = document.documentElement
    const set = (name: string, value: string) => root.style.setProperty(name, value)

    const mainBg = toRgbTriplet(consolidated.mainBg)
    const mainFg = toRgbTriplet(consolidated.mainFg)

    set('--main-bg', toSpaceTriplet(consolidated.mainBg))
    set('--main-fg', toSpaceTriplet(consolidated.mainFg))
    set('--button-bg', toSpaceTriplet(consolidated.buttonBg))
    set('--button-fg', toSpaceTriplet(consolidated.buttonFg))
    set('--sidebar-bg', toSpaceTriplet(consolidated.sidebarBg))
    set('--sidebar-fg', toSpaceTriplet(consolidated.sidebarFg))
    set('--sidebar-active', toSpaceTriplet(consolidated.sidebarTextActiveBorder))
    set('--link', toSpaceTriplet(consolidated.link))

    // upstream aliases, kept in the comma form the original SCSS expects
    set('--center-channel-bg-rgb', mainBg)
    set('--center-channel-color-rgb', mainFg)
    set('--sidebar-bg-rgb', toRgbTriplet(consolidated.sidebarBg))
    set('--sidebar-text-rgb', toRgbTriplet(consolidated.sidebarFg))
    set('--button-bg-rgb', toRgbTriplet(consolidated.buttonBg))

    root.dataset.theme = mainIsDark(consolidated) ? 'dark' : 'light'
    return consolidated
}

function mainIsDark(theme: Theme): boolean {
    const [r, g, b] = toRgbTriplet(theme.mainBg).split(',').map((v) => Number(v.trim()))
    if ([r, g, b].some((c) => Number.isNaN(c))) {
        return false
    }
    return (r * 299 + g * 587 + b * 114) / 1000 < 128
}

export function setTheme(theme: Theme | null): Theme {
    if (!theme) {
        window.localStorage.removeItem(Constants.localStorageKeys.theme)
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        activeThemeName = systemThemeName
        return applyTheme(prefersDark ? darkTheme : defaultTheme)
    }
    const consolidated = applyTheme(theme)
    window.localStorage.setItem(Constants.localStorageKeys.theme, JSON.stringify(theme))
    if (JSON.stringify(consolidated) === JSON.stringify(darkTheme)) {
        activeThemeName = darkThemeName
    } else if (JSON.stringify(consolidated) === JSON.stringify(lightTheme)) {
        activeThemeName = lightThemeName
    } else {
        activeThemeName = defaultThemeName
    }
    return consolidated
}

export function loadTheme(): Theme {
    const raw = window.localStorage.getItem(Constants.localStorageKeys.theme)
    if (raw) {
        try {
            return setTheme(JSON.parse(raw) as Theme)
        } catch {
            // fall through to system theme
        }
    }
    return setTheme(null)
}

export function initThemes(): void {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    mq.addEventListener('change', () => {
        if (!window.localStorage.getItem(Constants.localStorageKeys.theme)) {
            setTheme(null)
        }
    })
    loadTheme()
}

export const themeChoices: Array<{name: string, label: string, theme: Theme | null}> = [
    {name: defaultThemeName, label: '默认', theme: defaultTheme},
    {name: lightThemeName, label: '浅色', theme: lightTheme},
    {name: darkThemeName, label: '深色', theme: darkTheme},
    {name: systemThemeName, label: '跟随系统', theme: null},
]
